import '../css/style.css';

import Alpine from 'alpinejs';
import { intersect } from '@alpinejs/intersect';

Alpine.plugin(intersect);
window.Alpine = Alpine;

Alpine.data('siteHeader', () => ({
  active: false,
  showmenu: false,
  toggleMenu() {
    this.showmenu = !this.showmenu;
    if (this.showmenu) {
      this.$nextTick(() => {
        this.$refs.mobileNav.querySelector('a[href], button, input, textarea, select')?.focus();
      });
    }
  },
  closeMenu() {
    this.showmenu = false;
  },
  trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = this.$refs.mobileNav.querySelectorAll(
      'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  },
}));

if (document.querySelector('[data-fancybox]')) {
  import('./lib/fancybox.js').then(({ initFancybox }) => initFancybox());
}

Alpine.data('topBanner', () => ({
  async init() {
    const { Swiper, Autoplay, Pagination, EffectFade, A11y, a11yDefaults } = await import('./lib/swiper.js');
    const swiper = this.$el.querySelector('.swiper');
    const pager = this.$el.querySelector('.swiper-pager');
    const pagination = this.$el.querySelector('.swiper-pagination');

    this.swiper = new Swiper(swiper, {
      modules: [Autoplay, Pagination, EffectFade, A11y],
      a11y: a11yDefaults,
      loop: true,
      speed: 1000,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      pagination: {
        type: 'bullets',
        el: pagination,
        clickable: true,
      },
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      on: {
        slideChange: (swiper) => {
          pager.innerHTML = `0${swiper.realIndex + 1}`;
        },
      },
    });
  },
  swiper: null,
}));

Alpine.data('latestNews', () => ({
  async init() {
    const { Swiper, Autoplay, Pagination, EffectFade, A11y, a11yDefaults } = await import('./lib/swiper.js');
    const swiper = this.$el.querySelector('.swiper');
    const pagination = this.$el.querySelector('.swiper-pagination');

    this.swiper = new Swiper(swiper, {
      modules: [Autoplay, Pagination, EffectFade, A11y],
      a11y: a11yDefaults,
      autoHeight: true,
      loop: true,
      speed: 500,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      pagination: {
        type: 'bullets',
        el: pagination,
        clickable: true,
      },
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      on: {
        slideChange: (swiper) => {
          this.date = swiper.slides[swiper.realIndex].dataset.date;
        },
      },
    });
  },
  swiper: null,
  date: null,
}));

Alpine.data('workCard', () => ({
  async init() {
    this.img = this.$el.querySelector('img');
    if (!this.img) return;

    const { setAverageBgColor } = await import('./lib/color.js');
    setAverageBgColor(this.img, this.$el);
  },
  img: null,
}));

Alpine.data('workDetails', () => ({
  init() {
    this.detailsContent = document.querySelector('.details-content');
    this.headings = this.detailsContent.querySelectorAll('h2,h3,h4');
    if (this.headings.length) {
      this.headings.forEach((heading, index) => {
        heading.id = `heading-${index + 1}`;
      });
      this.$nextTick(() => {
        this.toc = true;
      });
    }
  },
  toc: false,
  detailsContent: null,
  headings: [],
}));

Alpine.data('blogDetails', () => ({
  init() {
    this.detailsContent = document.querySelector('.details-content');
    this.headings = this.detailsContent.querySelectorAll('h2,h3,h4');
    if (this.headings.length) {
      this.headings.forEach((heading, index) => {
        heading.id = `heading-${index + 1}`;
      });
      this.$nextTick(() => {
        this.toc = true;
      });
    }
  },
  toc: false,
  detailsContent: null,
  headings: [],
}));

Alpine.data('newsList', (items = []) => ({
  filter: null,
  items: items,
  filteredItems(filter) {
    if (this.filter === null) return this.items;
    return this.items.filter((item) =>
      item.category_slugs.includes(this.filter)
    );
  },
}));

Alpine.data('blogList', (items = []) => ({
  filter: null,
  items: items,
  filteredItems(filter) {
    if (this.filter === null) return this.items;
    return this.items.filter((item) =>
      item.category_slugs.includes(this.filter)
    );
  },
}));

Alpine.data('workList', (items = []) => ({
  filter: null,
  items: items,

  filteredItems(filter) {
    if (this.filter === null) return this.items;
    return this.items.filter((item) =>
      item.category_slugs.includes(this.filter)
    );
  },
}));

Alpine.data('worksSlider', () => ({
  async init() {
    const { Swiper, Navigation, A11y, a11yDefaults } = await import('./lib/swiper.js');
    const swiper = this.$el.querySelector('.swiper');
    const prevButton = this.$el.querySelector('.swiper-button-prev');
    const nextButton = this.$el.querySelector('.swiper-button-next');

    this.slider = new Swiper(swiper, {
      modules: [Navigation, A11y],
      a11y: a11yDefaults,
      loop: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      navigation: {
        prevEl: prevButton,
        nextEl: nextButton,
      },
      breakpoints: {
        768: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
      },
    });
  },
  slider: null,
}));

Alpine.data('voiceSlider', () => ({
  async init() {
    const { Swiper, Navigation, A11y, a11yDefaults } = await import('./lib/swiper.js');
    const swiper = this.$el.querySelector('.swiper');
    const prevButton = this.$el.querySelector('.swiper-button-prev');
    const nextButton = this.$el.querySelector('.swiper-button-next');

    this.slider = new Swiper(swiper, {
      modules: [Navigation, A11y],
      a11y: a11yDefaults,
      loop: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      navigation: {
        prevEl: prevButton,
        nextEl: nextButton,
      },
      breakpoints: {
        1024: {
          slidesPerView: 3,
          slidesPerGroup: 3,
        },
      },
    });
  },
  slider: null,
}));

Alpine.data('serviceGallery', () => ({
  async init() {
    const swiper = this.$el.querySelector('.swiper');
    if (!swiper) return;

    const { Swiper, Autoplay, A11y, a11yDefaults } = await import('./lib/swiper.js');
    this.slider = new Swiper(swiper, {
      modules: [Autoplay, A11y],
      a11y: a11yDefaults,
      loop: true,
      slidesPerView: 1,
      spaceBetween: 10,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      slideToClickedSlide: true,
      breakpoints: {
        768: {
          spaceBetween: 20,
          slidesPerView: 'auto',
          centeredSlides: true,
          centerInsufficientSlides: true,
        },
      },
    });
  },
  slider: null,
}));

Alpine.data('dragScroll', () => ({
  _dragging: false,
  _moved: false,
  _startX: 0,
  _scrollLeft: 0,
  _scroller: null,
  init() {
    this._scroller = this.$el.closest('.overflow-auto');
    this.$el.addEventListener('click', (e) => {
      if (this._moved) {
        e.preventDefault();
        return;
      }
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      e.preventDefault();
      const target = document.querySelector(a.getAttribute('href'));
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    }, true);
  },
  start(e) {
    this._dragging = true;
    this._moved = false;
    this._startX = e.clientX;
    this._scrollLeft = this._scroller.scrollLeft;
  },
  move(e) {
    if (!this._dragging) return;
    const delta = e.clientX - this._startX;
    if (Math.abs(delta) > 4) this._moved = true;
    this._scroller.scrollLeft = this._scrollLeft - delta;
  },
  end() {
    this._dragging = false;
  },
}));

Alpine.data('processSlider', () => ({
  async init() {
    const swiper = this.$el.querySelector('.swiper');
    const scrollbar = this.$el.querySelector('.swiper-scrollbar');
    if (!swiper) return;

    const { Swiper, FreeMode, Scrollbar, Mousewheel, A11y, a11yDefaults } = await import('./lib/swiper.js');
    this.slider = new Swiper(swiper, {
      modules: [FreeMode, Scrollbar, Mousewheel, A11y],
      a11y: a11yDefaults,
      slidesPerView: 'auto',
      freeMode: true,
      scrollbar: {
        el: scrollbar,
        draggable: true,
      },
      mousewheel: {
        forceToAxis: true,
      },
      slideToClickedSlide: true,
      spaceBetween: 40,
      breakpoints: {
        768: {
          spaceBetween: 80,
        },
      },
    });
  },
  slider: null,
}));

Alpine.start();
